// HospitalLinesCard — a hospital's own billing lines, the way the hospital
// names them (item 867, Ben's approved demo of 2026-08-28).
//
// ⛔ THE NAME IN THE FIRST COLUMN IS THE HOSPITAL'S. It says which of the
// hospital's own funding lines pays for the hours, and it is shown exactly as
// they write it (TCSD-0253). Everything TCS rolls up by — kind of work, role,
// shifts, basis — sits beside it and never replaces it.
//
// Layout follows the demo's Overland Park Regional ledger: line, role, basis,
// approved hours a week, actual hours, an hours track against what was
// approved, client rate, client bill, sub rate, sub cost, margin. Hourly lines
// fill toward a notch at the approved hours; flat, per-day and per-event lines
// get an empty dashed track ("hours not billed") rather than "n/a", which reads
// like missing data. The management fee is never on this card.
//
// Who sees what: the lines and their rates are everyone's, like a building's
// service lines. What a line BILLED comes from invoice lines, which are
// Finance's; anyone else sees the lines and one sentence saying where the
// billed figures are.
//
// Edits: one window per line, one Save, one statement (PLAYBOOK §15b). The two
// rates are NOT in it: each has its own button into the one rate door,
// record_rate_change(), so Finance is told and the change is recorded with a
// reason (TCSD-0188, Ben 2026-10-14 D2).
// ============================================================================

import { hasFinanceCapability } from '../../../shared/role-gates';
import { useMemo, useState } from 'react';
import { Receipt } from 'lucide-react';
import { Button } from '../../../shared/components';
import { useAuth } from '../../../shared/auth';

import { supabase } from '../../../shared/supabase';
import { commitWrite } from '../../../shared/save-path';
import { errorMessage } from '../../../shared/errors';
import { SmartSelect, isAssignableSub } from '../../../components/smart-select';
import { CardWithModeToggle } from './card-with-mode-toggle';
import { RateChangeModal } from './rate-change-modal';
import {
  useBillingLines,
  isManagementFeeLabel,
  type BillingBasis,
  type BillingLine,
  type CodeOption,
  type LineMonth,
} from '../hooks/use-billing-lines';

/** Weeks in an average month — the demo's hours track is built on it. */
const WEEKS_PER_MONTH = 52 / 12;

const BASIS_LABEL: Record<BillingBasis, string> = {
  Hourly: 'Hourly',
  Monthly_Flat: 'Monthly',
  Per_Day: 'Per day',
  Per_Event: 'Per event',
};
const BASIS_UNIT: Record<BillingBasis, string> = {
  Hourly: '/hr',
  Monthly_Flat: '/mo',
  Per_Day: '/day',
  Per_Event: '/event',
};

function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}
function hours(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function monthName(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function daysIn(iso: string): number {
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

/** What the sub costs for a month on this line, when it can be worked out honestly. */
export function subCostFor(line: BillingLine, actual: LineMonth | undefined, monthIso: string): number | null {
  if (line.sub_rate === null || !line.billing_basis) return null;
  switch (line.billing_basis) {
    case 'Hourly':
      return actual?.hours != null ? Math.round(line.sub_rate * actual.hours * 100) / 100 : null;
    case 'Monthly_Flat':
      return line.sub_rate;
    case 'Per_Day':
      return Math.round(line.sub_rate * daysIn(monthIso) * 100) / 100;
    case 'Per_Event':
      // We do not know how many events there were. Saying a number would be inventing one.
      return null;
  }
}

function Chip({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'hourly' | 'flat' }) {
  const cls =
    tone === 'hourly'
      ? 'bg-st-info-bg text-st-info-fg-strong'
      : tone === 'flat'
        ? 'bg-st-warn-bg text-st-warn-fg-strong'
        : 'bg-tcs-bg-muted text-tcs-text-secondary';
  return (
    <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] ${cls}`}>
      {children}
    </span>
  );
}

function HoursTrack({ line, actual }: { line: BillingLine; actual: LineMonth | undefined }) {
  const expected =
    line.billing_basis === 'Hourly' && line.authorized_hours_per_week
      ? line.authorized_hours_per_week * WEEKS_PER_MONTH
      : null;
  if (line.billing_basis !== 'Hourly') {
    return (
      <div className="ml-auto w-[104px] text-right">
        <div className="h-[17px] rounded-sm border border-dashed border-tcs-border bg-tcs-bg-muted" />
        <div className="mt-0.5 text-[10px] text-tcs-text-muted">hours not billed</div>
      </div>
    );
  }
  if (expected === null || actual?.hours == null) {
    return <span className="text-tcs-text-muted">—</span>;
  }
  const dev = (actual.hours - expected) / expected;
  const fill = Math.min(1, actual.hours / expected) * 100;
  const notch = actual.hours <= expected ? 99.5 : (expected / actual.hours) * 100;
  const tone = dev > 0 ? 'bg-st-warn-fg' : dev < -0.1 ? 'bg-st-danger-fg' : 'bg-st-info-fg';
  return (
    <div className="ml-auto w-[104px] text-right">
      <div className="relative h-[17px] overflow-hidden rounded-sm bg-tcs-bg-muted">
        <div className={`absolute inset-y-0 left-0 opacity-80 ${tone}`} style={{ width: `${fill}%` }} />
        <div className="absolute -bottom-0.5 -top-0.5 w-0.5 bg-tcs-text" style={{ left: `${notch}%` }} />
      </div>
      <div className={`mt-0.5 text-[10px] tabular-nums ${dev < -0.1 ? 'text-st-danger-fg-strong' : 'text-tcs-text-muted'}`}>
        {dev >= 0 ? '+' : '−'}
        {Math.abs(dev * 100).toFixed(1)}%{dev > 0 ? ' over' : ''}
      </div>
    </div>
  );
}

export function HospitalLinesCard({ facilityId }: { facilityId: string }) {
  const { profile } = useAuth();
  const finance = hasFinanceCapability(profile);
  const { lines, months, serviceLines, roles, loading, error, refresh } = useBillingLines(facilityId, finance);
  const [monthIdx, setMonthIdx] = useState(0);
  // The window reads the line from the latest load, by id, so a rate changed
  // from inside it shows at once (found walking the live page, 2026-10-14).
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = editingId ? lines.find((l) => l.facility_billing_line_id === editingId) ?? null : null;

  // ⛔ The management fee is Finance's alone and never on an account card.
  const shown = useMemo(() => lines.filter((l) => !isManagementFeeLabel(l.client_label)), [lines]);
  const month = finance ? months[Math.min(monthIdx, Math.max(0, months.length - 1))] : undefined;

  const totals = useMemo(() => {
    let approved = 0, actual = 0, expected = 0, billed = 0, cost = 0, costKnown = true;
    for (const l of shown) {
      const a = month?.byLine.get(l.facility_billing_line_id);
      if (l.billing_basis === 'Hourly' && l.authorized_hours_per_week) {
        approved += l.authorized_hours_per_week;
        if (a?.hours != null) expected += l.authorized_hours_per_week * WEEKS_PER_MONTH;
      }
      if (a?.hours != null && l.billing_basis === 'Hourly') actual += a.hours;
      if (a) {
        billed += a.billed;
        const c = month ? subCostFor(l, a, month.month) : null;
        if (c === null) costKnown = false;
        else cost += c;
      }
    }
    return { approved, actual, expected, billed, cost, costKnown };
  }, [shown, month]);

  const description = finance
    ? 'Named the way the hospital names them. Rates change through the rate door, so Finance is told and the reason is kept.'
    : 'Named the way the hospital names them. What each line billed is shown to Finance.';

  return (
    <div id="info-card-hospital-lines" className="scroll-mt-24">
      <CardWithModeToggle
        title="Hospital billing lines"
        icon={<Receipt className="h-4 w-4 text-tcs-text-secondary" />}
        mode="view"
        onModeChange={() => {}}
        readOnly
        description={description}
      >
        {loading ? (
          <p className="text-sm text-tcs-text-muted">Loading the hospital's lines…</p>
        ) : error ? (
          <p className="rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong">{error}</p>
        ) : shown.length === 0 ? (
          <p className="text-sm text-tcs-text-secondary">
            No billing lines are recorded for this hospital yet. Its invoice lines are on the worklist for the
            sitting with Jose and the account manager.
          </p>
        ) : (
          <div className="space-y-3">
            {finance && months.length > 0 && month && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" disabled={monthIdx >= months.length - 1} onClick={() => setMonthIdx((i) => i + 1)} aria-label="Earlier month">‹</Button>
                  <span className="text-sm font-semibold text-tcs-text">{monthName(month.month)}</span>
                  <Button variant="secondary" size="sm" disabled={monthIdx === 0} onClick={() => setMonthIdx((i) => Math.max(0, i - 1))} aria-label="Later month">›</Button>
                </div>
                {month.unplacedCount > 0 && (
                  <span className="text-xs text-st-warn-fg-strong">
                    {month.unplacedCount} invoice line{month.unplacedCount === 1 ? '' : 's'} ({money(month.unplacedAmount)}) this month
                    {' '}are not on a line yet — they are on the worklist, not guessed.
                  </span>
                )}
              </div>
            )}
            {finance && months.length === 0 && (
              <p className="text-xs text-tcs-text-muted">No billed months are recorded for this hospital yet.</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-tcs-border text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                    <th className="px-2 py-2 text-left">The hospital's line name</th>
                    <th className="px-2 py-2 text-left">Role</th>
                    <th className="px-2 py-2 text-left">Basis</th>
                    <th className="px-2 py-2 text-right">Approved hrs/wk</th>
                    {finance && <th className="px-2 py-2 text-right">Actual hrs</th>}
                    {finance && <th className="px-2 py-2 text-right">vs approved</th>}
                    <th className="px-2 py-2 text-right">Client rate</th>
                    {finance && <th className="px-2 py-2 text-right">Client bill</th>}
                    <th className="px-2 py-2 text-right">Contract rate</th>
                    {finance && <th className="px-2 py-2 text-right">Sub cost</th>}
                    {finance && <th className="px-2 py-2 text-right">Margin %</th>}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((l) => {
                    const a = month?.byLine.get(l.facility_billing_line_id);
                    const cost = month ? subCostFor(l, a, month.month) : null;
                    const margin = a && a.billed > 0 && cost !== null ? ((a.billed - cost) / a.billed) * 100 : null;
                    const unit = l.billing_basis ? BASIS_UNIT[l.billing_basis] : '';
                    return (
                      <tr key={l.facility_billing_line_id} className={`border-b border-tcs-border ${l.status === 'Ended' ? 'opacity-60' : ''}`}>
                        <td className={`px-2 py-2 text-left ${l.billing_basis === 'Hourly' ? 'border-l-[3px] border-l-st-info-fg' : 'border-l-[3px] border-l-st-warn-fg'}`}>
                          <button type="button" onClick={() => setEditingId(l.facility_billing_line_id)} className="text-left font-semibold text-tcs-text hover:underline">
                            {l.client_label}
                          </button>
                          <div className="text-[11px] text-tcs-text-muted">
                            {[l.service_line_label ?? 'kind of work not set', l.shift_scope, l.provider_name, l.status !== 'Active' ? l.status.toLowerCase() : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-left">{l.role_label ? <Chip>{l.role_label}</Chip> : <span className="text-tcs-text-muted">—</span>}</td>
                        <td className="px-2 py-2 text-left">
                          {l.billing_basis ? <Chip tone={l.billing_basis === 'Hourly' ? 'hourly' : 'flat'}>{BASIS_LABEL[l.billing_basis]}</Chip> : <span className="text-tcs-text-muted">—</span>}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">{l.billing_basis === 'Hourly' ? hours(l.authorized_hours_per_week) : <span className="text-tcs-text-muted">n/a</span>}</td>
                        {finance && <td className="px-2 py-2 text-right tabular-nums">{l.billing_basis === 'Hourly' ? hours(a?.hours) : <span className="text-tcs-text-muted">n/a</span>}</td>}
                        {finance && <td className="px-2 py-2"><HoursTrack line={l} actual={a} /></td>}
                        <td className="px-2 py-2 text-right tabular-nums">{l.billing_rate !== null ? `${money(l.billing_rate)}${unit}` : <span className="text-xs text-tcs-text-muted">not set</span>}</td>
                        {finance && <td className="px-2 py-2 text-right tabular-nums">{a ? money(a.billed) : '—'}</td>}
                        <td className="px-2 py-2 text-right tabular-nums">{l.sub_rate !== null ? `${money(l.sub_rate)}${unit}` : <span className="text-tcs-text-muted">—</span>}</td>
                        {finance && <td className="px-2 py-2 text-right tabular-nums">{money(cost)}</td>}
                        {finance && <td className="px-2 py-2 text-right tabular-nums">{margin !== null ? `${margin.toFixed(1)}%` : '—'}</td>}
                      </tr>
                    );
                  })}
                </tbody>
                {finance && month && (
                  <tfoot>
                    <tr className="border-t-2 border-tcs-text font-bold">
                      <td className="px-2 py-2 text-left">Billed lines · {monthName(month.month)}</td>
                      <td />
                      <td />
                      <td className="px-2 py-2 text-right tabular-nums">{totals.approved > 0 ? hours(totals.approved) : '—'}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{totals.actual > 0 ? hours(totals.actual) : '—'}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {totals.expected > 0 ? `${totals.actual >= totals.expected ? '+' : '−'}${Math.abs(((totals.actual - totals.expected) / totals.expected) * 100).toFixed(1)}%` : '—'}
                      </td>
                      <td />
                      <td className="px-2 py-2 text-right tabular-nums">{money(totals.billed)}</td>
                      <td />
                      <td className="px-2 py-2 text-right tabular-nums">{totals.costKnown ? money(totals.cost) : `${money(totals.cost)}+`}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {totals.billed > 0 && totals.costKnown ? `${(((totals.billed - totals.cost) / totals.billed) * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {finance && month && !totals.costKnown && (
              <p className="text-xs text-tcs-text-muted">
                Sub cost is left out where a line has no sub rate yet, or bills per event, so the total is a floor (shown with +).
              </p>
            )}
          </div>
        )}
      </CardWithModeToggle>

      {editing && (
        <BillingLineWindow
          line={editing}
          serviceLines={serviceLines}
          roles={roles}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            refresh();
          }}
          onRateChanged={refresh}
        />
      )}
    </div>
  );
}

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

function numOrNull(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function BillingLineWindow({
  line,
  serviceLines,
  roles,
  onClose,
  onSaved,
  onRateChanged,
}: {
  line: BillingLine;
  serviceLines: CodeOption[];
  roles: CodeOption[];
  onClose: () => void;
  onSaved: () => void;
  onRateChanged: () => void;
}) {
  const [label, setLabel] = useState(line.client_label);
  const [kind, setKind] = useState(line.service_line_code ?? '');
  const [role, setRole] = useState(line.role_code ?? '');
  const [shift, setShift] = useState(line.shift_scope ?? '');
  const [basis, setBasis] = useState<string>(line.billing_basis ?? '');
  const [approved, setApproved] = useState(line.authorized_hours_per_week?.toString() ?? '');
  const [providerId, setProviderId] = useState<string | null>(line.provider_id);
  const [status, setStatus] = useState(line.status);
  const [start, setStart] = useState(line.effective_start ?? '');
  const [end, setEnd] = useState(line.effective_end ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rateField, setRateField] = useState<'billing_rate' | 'sub_rate' | null>(null);

  const patch: Record<string, string | number | null> = {};
  const trimmedLabel = label.trim();
  if (trimmedLabel !== line.client_label) patch.client_label = trimmedLabel;
  if ((kind || null) !== line.service_line_code) patch.service_line_code = kind || null;
  if ((role || null) !== line.role_code) patch.role_code = role || null;
  if ((shift.trim() || null) !== line.shift_scope) patch.shift_scope = shift.trim() || null;
  if ((basis || null) !== line.billing_basis) patch.billing_basis = basis || null;
  if (numOrNull(approved) !== line.authorized_hours_per_week) patch.authorized_hours_per_week = numOrNull(approved);
  if (providerId !== line.provider_id) patch.provider_id = providerId;
  if (status !== line.status) patch.status = status;
  if ((start || null) !== line.effective_start) patch.effective_start = start || null;
  if ((end || null) !== line.effective_end) patch.effective_end = end || null;
  const changed = Object.keys(patch).length > 0;

  async function save() {
    if (!trimmedLabel) {
      setErr("The hospital's name for the line cannot be blank.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      // ONE statement, so one audit row names every column changed together.
      await commitWrite({
        label: `save the line "${line.client_label}"`,
        write: supabase
          .from('facility_billing_lines')
          .update(patch)
          .eq('facility_billing_line_id', line.facility_billing_line_id)
          .select('facility_billing_line_id'),
        touches: ['facility_billing_lines'],
      });
      onSaved();
    } catch (e) {
      setErr(errorMessage(e, 'The line could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const unit = line.billing_basis ? BASIS_UNIT[line.billing_basis] : '';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center" onClick={onClose}>
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-xl bg-tcs-overlay shadow-xl sm:max-h-[88vh] sm:w-[560px] sm:rounded-xl"
          role="dialog"
          aria-modal="true"
        >
          <header className="border-b border-tcs-border bg-tcs-bg p-4">
            <h2 className="text-base font-semibold text-tcs-text">Edit — {line.client_label}</h2>
            <p className="mt-0.5 text-xs text-tcs-text-secondary">
              The name is the hospital's own. Change it only when the hospital renames the department.
            </p>
          </header>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-tcs-text">The hospital calls it</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} className={INPUT} />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">Our kind of work</span>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className={INPUT}>
                  <option value="">Not set</option>
                  {serviceLines.map((o) => (<option key={o.code} value={o.code}>{o.label}</option>))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">Role</span>
                <select value={role} onChange={(e) => setRole(e.target.value)} className={INPUT}>
                  <option value="">Not set</option>
                  {roles.map((o) => (<option key={o.code} value={o.code}>{o.label}</option>))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">Shifts, as the hospital says them</span>
                <input value={shift} onChange={(e) => setShift(e.target.value)} placeholder="1st & 2nd, All Shifts…" className={INPUT} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">How it bills</span>
                <select value={basis} onChange={(e) => setBasis(e.target.value)} className={INPUT}>
                  <option value="">Not set</option>
                  {(Object.keys(BASIS_LABEL) as BillingBasis[]).map((b) => (<option key={b} value={b}>{BASIS_LABEL[b]}</option>))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">Approved hours a week</span>
                <input type="number" inputMode="decimal" min="0" step="0.25" value={approved} onChange={(e) => setApproved(e.target.value)} className={INPUT} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value as BillingLine['status'])} className={INPUT}>
                  <option value="Active">Active</option>
                  <option value="Paused">Paused</option>
                  <option value="Ended">Ended</option>
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">From</span>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={INPUT} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-tcs-text">To</span>
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={INPUT} />
              </label>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-tcs-text">Sub on this line</span>
              <SmartSelect entity="service_provider" value={providerId} onChange={(id) => setProviderId(id)} entityLabel="sub" placeholder="Search subs…" isSelectable={isAssignableSub} />
            </label>

            <div className="rounded-md border border-tcs-border p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">Changed through the rate door</p>
              <div className="mt-2 flex items-center justify-between gap-2 text-sm">
                <span>What the client pays: <b className="tabular-nums">{line.billing_rate !== null ? `${money(line.billing_rate)}${unit}` : 'not set'}</b></span>
                <Button variant="secondary" size="sm" onClick={() => setRateField('billing_rate')}>Change</Button>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 text-sm">
                <span>Subcontractor contract rate: <b className="tabular-nums">{line.sub_rate !== null ? `${money(line.sub_rate)}${unit}` : 'not set'}</b></span>
                <Button variant="secondary" size="sm" onClick={() => setRateField('sub_rate')}>Change</Button>
              </div>
            </div>
            {line.notes && <p className="text-xs text-tcs-text-muted">{line.notes}</p>}
            {err && <p className="rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong">{err}</p>}
          </div>

          <footer className="flex justify-end gap-2 border-t border-tcs-border bg-tcs-bg p-3">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} disabled={saving || !changed}>{saving ? 'Saving…' : 'Save'}</Button>
          </footer>
        </div>
      </div>
      {rateField && (
        <RateChangeModal
          open
          onClose={() => setRateField(null)}
          assignment_id={line.facility_billing_line_id}
          field_name={rateField}
          field_label={rateField === 'billing_rate' ? 'client rate' : 'sub rate'}
          current_value={rateField === 'billing_rate' ? line.billing_rate : line.sub_rate}
          onRecorded={() => {
            onRateChanged();
          }}
        />
      )}
    </>
  );
}
